// Bandwidth-saving helpers for the player engine.
//
// All functions are pure (or pure-ish; the throttle is stateful but isolated).
// Unit tests drive them with synthetic clocks so the engine itself can stay
// minimal.

import type { ScreenBandwidthSettingsV1 } from '@/types/screen-settings';

/** Default sync interval when nothing is configured (current engine behavior). */
export const DEFAULT_SYNC_INTERVAL_MS = 5 * 60_000;
/** Floor on the configurable sync interval, regardless of UI input. */
export const MIN_SYNC_INTERVAL_MS = 60_000;
/** Ceiling on the configurable sync interval, regardless of UI input. */
export const MAX_SYNC_INTERVAL_MS = 24 * 60 * 60_000;

/**
 * Resolve the sync interval (ms) the engine should use given the settings.
 * Falls back to the engine default when unset.
 */
export function effectiveSyncIntervalMs(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
): number {
  const minutes = settings?.syncIntervalMinutes;
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) {
    return DEFAULT_SYNC_INTERVAL_MS;
  }
  const ms = Math.round(minutes * 60_000);
  return Math.max(MIN_SYNC_INTERVAL_MS, Math.min(MAX_SYNC_INTERVAL_MS, ms));
}

/**
 * True iff `now` (interpreted in the device's local timezone) falls inside
 * the half-open hour window `[startHour, endHour)`. Handles cross-midnight
 * windows where `endHour <= startHour` by treating them as wrapping past
 * midnight (e.g. 22→6 = 22:00 today through 06:00 tomorrow).
 *
 * Returns false when either bound is missing or out of range.
 */
export function inHourWindow(
  now: Date,
  startHour: number | undefined,
  endHour: number | undefined,
): boolean {
  if (typeof startHour !== 'number' || typeof endHour !== 'number') return false;
  if (!Number.isInteger(startHour) || startHour < 0 || startHour > 23) return false;
  if (!Number.isInteger(endHour) || endHour < 0 || endHour > 23) return false;
  if (startHour === endHour) return false;
  const hour = now.getHours();
  if (startHour < endHour) {
    return hour >= startHour && hour < endHour;
  }
  // Cross-midnight: e.g. 22→6 means hour >= 22 OR hour < 6.
  return hour >= startHour || hour < endHour;
}

/**
 * True iff the given moment falls inside the configured quiet-hours window.
 * Quiet hours pause new asset downloads but never affect the currently-
 * playing revision.
 */
export function isQuietNow(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
  now: Date,
): boolean {
  const w = settings?.quietHours;
  if (!w?.enabled) return false;
  return inHourWindow(now, w.startHour, w.endHour);
}

/**
 * True iff the given moment falls inside the configured pre-download window.
 * The pre-download window is permissive: it forces caching even when quiet
 * hours would otherwise apply.
 */
export function isPreDownloadNow(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
  now: Date,
): boolean {
  const w = settings?.preDownload;
  if (!w?.enabled) return false;
  return inHourWindow(now, w.startHour, w.endHour);
}

/**
 * Decide whether the engine should download new assets right now. Pre-download
 * always wins; otherwise quiet hours block; otherwise allow.
 */
export function shouldDownloadNow(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
  now: Date,
): boolean {
  if (isPreDownloadNow(settings, now)) return true;
  if (isQuietNow(settings, now)) return false;
  return true;
}

/**
 * Resolve the maximum number of in-flight downloads. Defaults to 1 — matches
 * the existing sequential behavior so an upgrade is a no-op for screens that
 * never set this.
 */
export function effectiveConcurrency(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
): number {
  const v = settings?.bandwidth?.maxConcurrentDownloads;
  if (typeof v !== 'number' || !Number.isFinite(v)) return 1;
  return Math.max(1, Math.min(8, Math.round(v)));
}

/**
 * Resolve the throughput ceiling in bytes/second. 0 / undefined = unlimited.
 */
export function effectiveMaxBytesPerSecond(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
): number {
  const v = settings?.bandwidth?.maxBytesPerSecond;
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return 0;
  return Math.round(v);
}

// ---------------------------------------------------------------------------
// Stateful download gate: enforces both a concurrency cap and a token-bucket
// byte rate. The engine wraps each fetch with `throttle.acquire()` /
// `throttle.report(bytes)` so existing IO logic stays untouched.
// ---------------------------------------------------------------------------

/** Injected for tests. */
export interface ThrottleClock {
  now: () => number;
  /** Resolve after `ms` milliseconds. */
  sleep: (ms: number) => Promise<void>;
}

const realClock: ThrottleClock = {
  now: () => Date.now(),
  sleep: (ms) =>
    new Promise<void>((resolve) => {
      // setTimeout returns NodeJS.Timeout in node and number in browser; cast.
      setTimeout(resolve, Math.max(0, ms));
    }),
};

export interface BandwidthThrottleOptions {
  /** Max concurrent downloads. Default 1. */
  maxConcurrent?: number;
  /** Max bytes/sec across the whole engine. 0 = unlimited. */
  maxBytesPerSecond?: number;
  /** Injected for tests. */
  clock?: ThrottleClock;
}

/**
 * Cooperative throttle for media downloads. Two independent levers:
 *   - concurrency cap: at most N in-flight fetches at once
 *   - rate cap: after each download, optionally sleep so the moving
 *     average over the previous second never exceeds maxBytesPerSecond
 *
 * The rate cap is intentionally simple — we don't try to slice individual
 * responses into chunks (which would require Streams), we just delay the next
 * fetch by enough time to amortize the bytes that just landed. Imperfect on
 * single huge files but predictable across a playlist of small/medium ones.
 */
export class BandwidthThrottle {
  private readonly clock: ThrottleClock;
  private maxConcurrent: number;
  private maxBytesPerSecond: number;
  private inFlight = 0;
  private waiters: Array<() => void> = [];
  /** Wall-clock time at/after which the next fetch may start. */
  private nextSlotAt = 0;

  constructor(options: BandwidthThrottleOptions = {}) {
    this.clock = options.clock ?? realClock;
    this.maxConcurrent = Math.max(1, Math.min(8, options.maxConcurrent ?? 1));
    this.maxBytesPerSecond = Math.max(0, options.maxBytesPerSecond ?? 0);
  }

  /** Hot-update the policy without re-instantiating. */
  update(options: BandwidthThrottleOptions): void {
    this.maxConcurrent = Math.max(1, Math.min(8, options.maxConcurrent ?? this.maxConcurrent));
    this.maxBytesPerSecond = Math.max(
      0,
      options.maxBytesPerSecond ?? this.maxBytesPerSecond,
    );
    // Wake any waiters that the new (higher) concurrency cap can now admit.
    while (this.inFlight < this.maxConcurrent && this.waiters.length > 0) {
      const next = this.waiters.shift();
      if (next) {
        this.inFlight += 1;
        next();
      }
    }
  }

  /**
   * Reserve a download slot. Resolves once both the concurrency cap and the
   * rate cap permit a new fetch. Caller must call `release()` when the fetch
   * is finished, even on error.
   */
  async acquire(): Promise<void> {
    if (this.inFlight < this.maxConcurrent) {
      this.inFlight += 1;
    } else {
      await new Promise<void>((resolve) => {
        this.waiters.push(resolve);
      });
    }

    // Rate-limit gate: if a previous fetch reported bytes that pushed
    // `nextSlotAt` into the future, sleep until that moment.
    const wait = this.nextSlotAt - this.clock.now();
    if (wait > 0) {
      await this.clock.sleep(wait);
    }
  }

  /**
   * Report `bytes` downloaded by the slot we just held, then release the
   * slot. Combines reporting + release into a single call so the engine
   * can't accidentally release without reporting.
   */
  release(bytes: number): void {
    if (this.maxBytesPerSecond > 0 && bytes > 0) {
      const now = this.clock.now();
      const sleepMs = Math.ceil((bytes / this.maxBytesPerSecond) * 1000);
      // Schedule the *next* fetch slot. If multiple slots are currently in
      // flight, their `acquire` wait stacks because we advance from
      // max(now, nextSlotAt).
      this.nextSlotAt = Math.max(now, this.nextSlotAt) + sleepMs;
    }
    if (this.waiters.length > 0) {
      const next = this.waiters.shift();
      if (next) {
        // Concurrency stays the same: we hand the slot directly to the next
        // waiter rather than decrement+increment.
        next();
        return;
      }
    }
    this.inFlight = Math.max(0, this.inFlight - 1);
  }

  /** Diagnostics only — not part of the public contract. */
  get _state() {
    return {
      inFlight: this.inFlight,
      waiters: this.waiters.length,
      nextSlotAt: this.nextSlotAt,
      maxConcurrent: this.maxConcurrent,
      maxBytesPerSecond: this.maxBytesPerSecond,
    };
  }
}

/**
 * Build a throttle from a settings document. Exposed so the engine can pass
 * the same object into multiple downloads without recomputing.
 */
export function buildThrottle(
  settings: ScreenBandwidthSettingsV1 | undefined | null,
  clock?: ThrottleClock,
): BandwidthThrottle {
  return new BandwidthThrottle({
    maxConcurrent: effectiveConcurrency(settings),
    maxBytesPerSecond: effectiveMaxBytesPerSecond(settings),
    clock,
  });
}
